interface BrandTourData {
  showGrandTour: boolean;
  signupDate: string;
}

const TOUR_DURATION_DAYS = 14; // 2 weeks

export const brandTourManager = {
  /**
   * Get tour data from localStorage
   */
  getTourData(): BrandTourData | null {
    try {
      const data = localStorage.getItem("brandTourData");
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error("Error reading brand tour data:", error);
      return null;
    }
  },

  /**
   * Set tour data to localStorage
   */
  setTourData(data: BrandTourData): void {
    try {
      localStorage.setItem("brandTourData", JSON.stringify(data));
    } catch (error) {
      console.error("Error saving brand tour data:", error);
    }
  },

  /**
   * Initialize tour data for new brand signup
   */
  initializeTourData(): void {
    const tourData: BrandTourData = {
      showGrandTour: true,
      signupDate: new Date().toISOString(),
    };
    this.setTourData(tourData);
  },

  /**
   * Check if grand tour should be shown based on signup date
   */
  shouldShowGrandTour(): boolean {
    const tourData = this.getTourData();
    if (!tourData) return false;

    const signupDate = new Date(tourData.signupDate);
    const currentDate = new Date();
    const daysSinceSignup = Math.floor(
      (currentDate.getTime() - signupDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    // Show grand tour for 2 weeks after signup
    if (daysSinceSignup <= TOUR_DURATION_DAYS) {
      return tourData.showGrandTour;
    }

    // After 2 weeks, hide the tour and update the data
    if (tourData.showGrandTour) {
      this.setTourData({
        ...tourData,
        showGrandTour: false,
      });
    }

    return false;
  },

  /**
   * Hide the grand tour (user manually dismissed it)
   */
  hideGrandTour(): void {
    const tourData = this.getTourData();
    if (tourData) {
      this.setTourData({
        ...tourData,
        showGrandTour: false,
      });
    }
  },

  /**
   * Get days remaining for tour display
   */
  getDaysRemaining(): number {
    const tourData = this.getTourData();
    if (!tourData) return 0;

    const signupDate = new Date(tourData.signupDate);
    const currentDate = new Date();
    const daysSinceSignup = Math.floor(
      (currentDate.getTime() - signupDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    return Math.max(0, TOUR_DURATION_DAYS - daysSinceSignup);
  },

  /**
   * Reset tour data (for testing or admin purposes)
   */
  resetTourData(): void {
    localStorage.removeItem("brandTourData");
  },
};
